import { colors } from '@theme/colors';

import { Components, Theme } from '@mui/material/styles';
import { SystemMode } from '@mui/system/cssVars/useCurrentColorScheme';

const BORDER_RADIUS = 4;
const BG_COLOR_TRANSPARENT = 'transparent';

export const themeComponents = (
  mode: SystemMode = 'light'
): Components<Omit<Theme, 'components'>> => ({
  MuiButton: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: '4px',
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: `${BORDER_RADIUS}px`,
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        color: colors.grey900,
        padding: '24px',
      },
      title: {
        fontSize: '1.125rem',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '24px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        alignItems: 'center',
      },
      outlined: {
        border: '1px dashed',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        color: colors.grey700,
        paddingTop: '10px',
        paddingBottom: '10px',
        '&.Mui-selected': {
          color: colors.secondaryDark,
          backgroundColor: colors.secondaryLight,
          '&:hover': {
            backgroundColor: colors.secondaryLight,
          },
          '& .MuiListItemIcon-root': {
            color: colors.secondaryDark,
          },
        },
        '&:hover': {
          backgroundColor: colors.secondaryLight,
          color: colors.secondaryDark,
          '& .MuiListItemIcon-root': {
            color: colors.secondaryDark,
          },
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: colors.grey700,
        minWidth: '36px',
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        color: colors.grey900,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        color: colors.grey900,
        '&::placeholder': {
          color: colors.grey500,
          fontSize: '0.875rem',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        background: BG_COLOR_TRANSPARENT,
        borderRadius: `${BORDER_RADIUS}px`,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'dark' ? colors.grey700 + 28 : colors.grey400,
        },
        '&:hover $notchedOutline': {
          borderColor: colors.primaryLight,
        },
        '&.MuiInputBase-multiline': {
          padding: 1,
        },
      },
      input: {
        fontWeight: 500,
        background: BG_COLOR_TRANSPARENT,
        padding: '15.5px 14px',
        borderRadius: `${BORDER_RADIUS}px`,
        '&.MuiInputBase-inputSizeSmall': {
          padding: '10px 14px',
          '&.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
        },
      },
      inputAdornedStart: {
        paddingLeft: 4,
      },
      notchedOutline: {
        borderRadius: `${BORDER_RADIUS}px`,
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: mode === 'dark' ? colors.grey700 + 50 : colors.grey300,
        },
      },
      mark: {
        backgroundColor: colors.paper,
        width: '4px',
      },
      valueLabel: {
        color: mode === 'dark' ? colors.primaryMain : colors.primaryLight,
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiAutocomplete-tag': {
          background:
            mode === 'dark' ? colors.grey700 + 20 : colors.secondaryLight,
          borderRadius: 4,
          color: colors.grey900,
          '.MuiChip-deleteIcon': {
            color: mode === 'dark' ? colors.grey700 + 80 : colors.secondary200,
          },
        },
      },
      popper: {
        borderRadius: `${BORDER_RADIUS}px`,
        boxShadow:
          '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: colors.grey200,
        opacity: mode === 'dark' ? 0.2 : 1,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        /** checked not prop
         *"&.Mui-checked": {
         *    fontSize: "28px"
         *}
         */
      },
    },
  },
  MuiAvatar: {
    variants: [
      {
        props: { variant: 'commonAvatar' },
        style: {
          cursor: 'pointer',
          borderRadius: '8px',
        },
      },
      {
        props: { variant: 'smallAvatar' },
        style: {
          width: '22px',
          height: '22px',
          fontSize: '1rem',
        },
      },
      {
        props: { variant: 'mediumAvatar' },
        style: {
          width: '34px',
          height: '34px',
          fontSize: '1.2rem',
        },
      },
      {
        props: { variant: 'largeAvatar' },
        style: {
          width: '44px',
          height: '44px',
          fontSize: '1.5rem',
        },
      },
    ],
    styleOverrides: {
      root: {
        color: mode === 'dark' ? colors.darkLevel1 : colors.primaryDark,
        background: mode === 'dark' ? colors.grey700 : colors.primary200,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        '&.MuiChip-deletable .MuiChip-deleteIcon': {
          color: 'inherit',
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      flexContainer: {
        borderBottom: '1px solid',
        borderColor: mode === 'dark' ? colors.grey700 + 20 : colors.grey200,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        padding: '12px 0 12px 0',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: mode === 'dark' ? colors.grey700 + 15 : colors.grey200,
        '&.MuiTableCell-head': {
          fontSize: '0.875rem',
          color: colors.grey900,
          fontWeight: 500,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        color: mode === 'dark' ? colors.darkLevel1 : colors.paper,
        background: mode === 'dark' ? colors.grey50 : colors.grey700,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.25rem',
      },
    },
  },
});
